import React, { useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import {
  Gauge, Users, MapTrifold, ChartLine, ChatsCircle, SignOut, Buildings,
  ShieldCheck, Certificate, Leaf, FileText, User,
  AirplaneTilt, Coins,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/gov", label: "Regulatory Dashboard", icon: Gauge, end: true },
  { to: "/gov/customers", label: "Jurisdiction Analytics", icon: Users },
  { to: "/gov/bmw", label: "BMW Waste Reports", icon: ChartLine },
  { to: "/gov/compliance", label: "Compliance Monitoring", icon: ShieldCheck },
  { to: "/gov/airq", label: "AirQ · DGCA", icon: AirplaneTilt },
  { to: "/gov/csr", label: "CSR Portal", icon: Coins },
  { to: "/gov/audit", label: "Audit Trails", icon: FileText },
  { to: "/gov/certificates", label: "Certificate Verification", icon: Certificate },
  { to: "/gov/sustainability", label: "Sustainability Metrics", icon: Leaf },
  { to: "/gov/complaints", label: "Public Complaints", icon: ChatsCircle },
  { to: "/gov/map", label: "Compliance Map", icon: MapTrifold },
  { to: "/gov/profile", label: "User Profile", icon: User },
];

export default function GovLayout() {
  const { user } = useAuth();
  const nav = useNavigate();
  useEffect(() => {
    if (!user) nav("/gov/login", { replace: true });
  }, [user, nav]);

  // Gov logout: clean up the admin-auth tokens (same storage) but route
  // back to /gov/login — never /adentry.
  const govSignOut = async () => {
    try { const { api } = await import("../../lib/api"); await api.post("/auth/logout"); }
    catch { /* ignore */ }
    localStorage.removeItem("rescigo_token");
    localStorage.removeItem("rescigo_user");
    window.location.href = "/gov/login";
  };

  if (!user) return null;
  if (user.role !== "govt") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F6F2]">
        <div className="text-center">
          <div className="font-heading text-2xl">Access restricted</div>
          <div className="text-sm text-[#5C5A55] mt-1">This portal is only for Government users.</div>
          <button onClick={() => nav("/dashboard")} className="mt-4 px-4 h-10 bg-[#2D5A27] text-white rounded-md text-sm">Go to admin</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F6F2] flex" data-testid="gov-layout">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-[#E5E2D9] flex flex-col">
        <div className="px-5 py-4 border-b border-[#E5E2D9]">
          <div className="flex items-center gap-2">
            <Buildings size={20} color="#2D5A27" weight="duotone"/>
            <div>
              <div className="font-heading text-lg leading-tight">Gov Portal</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">Read-only</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-2 space-y-1">
          {NAV.map(n => (
            <NavLink key={n.to} to={n.to} end={n.end}
                     data-testid={`gov-nav-${n.label.toLowerCase().replace(/\s+/g,'-')}`}
                     className={({isActive}) =>
                       `flex items-center gap-2 px-3 h-10 rounded-md text-sm ${isActive ? "bg-[#2D5A27] text-white" : "text-[#2D2B26] hover:bg-[#F7F6F2]"}`
                     }>
              <n.icon size={16} weight="duotone"/>{n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-[#E5E2D9] text-xs text-[#5C5A55]">
          <div className="font-medium">{user.name}</div>
          <div className="text-[10px] text-[#8A8780]">{user.email}</div>
          <button onClick={govSignOut} data-testid="gov-logout"
                  className="mt-2 w-full inline-flex items-center justify-center gap-1 h-9 border border-[#E5E2D9] rounded-md text-xs hover:bg-[#F7F6F2]">
            <SignOut size={12}/>Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 p-8 overflow-auto">
        <Outlet/>
      </main>
    </div>
  );
}
